// src/features/cashRegister/api/cashFlowApi.ts

import { fetchData } from '../../../../utils/fetch';

/** Shape of a cash‐register as returned by the backend */
export interface CashRegister {
  /** Unique register ID */
  id_register:      number;
  /** Optional human‐readable name */
  register_name?:   string;
  /** Whether this register is currently active */
  is_active:        boolean;
  /** When the register itself was created */
  created_at:       string;

  // — newly added by your backend GET /registers:
  /** Timestamp when the last shift was opened (if any) */
  last_opened?:     string | null;
  /** Timestamp when the last shift was closed (if any) */
  last_closed?:     string | null;
  /** ID of the last shift (if any) */
  last_shift_id?:   number;
  /** Whether that last shift is still open */
  last_shift_open?: boolean;
}

/** Payload when creating a new register */
export interface NewRegisterPayload {
  register_name?: string;
}

/**
 * Create a new cash register.
 * POST /service_cash_flow/register
 */
export async function createRegister(
  payload: NewRegisterPayload
): Promise<CashRegister> {
  const res = await fetchData('/service_cash_flow/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  // service returns { id_register, register_name?, timestamp, execution_time_ms }
  const data = Array.isArray(res) ? res[0] : res;

  return {
    id_register:    data.id_register,
    register_name:  data.register_name,
    is_active:      true, // new registers are active by default
    created_at:     data.timestamp ?? String(data.execution_time_ms ?? ''),

    // the backend won’t set these until you open/close a shift:
    last_opened:    data.last_opened    ?? null,
    last_closed:    data.last_closed    ?? null,
    last_shift_id:  data.last_shift_id  ?? null,
    last_shift_open:data.last_shift_open ?? false,
  };
}

/**
 * Fetch all cash registers.
 * GET /service_cash_flow/registers?active_only=true|false
 */
export async function fetchRegisters(
  activeOnly: boolean = false
): Promise<CashRegister[]> {
  const suffix = activeOnly ? '?active_only=true' : '';
  const res = await fetchData(`/service_cash_flow/registers${suffix}`, {
    method: 'GET',
  });
  // service returns { cash_registers: [...], execution_time_ms }
  const list = Array.isArray(res)
    ? res
    : Array.isArray((res as any).cash_registers)
    ? (res as any).cash_registers
    : [];

  return (list as any[]).map(rec => ({
    id_register:    rec.id_register,
    register_name:  rec.register_name,
    is_active:      rec.active,
    created_at:     rec.timestamp,

    // newly-added:
    last_opened:    rec.last_opened    ?? null,
    last_closed:    rec.last_closed    ?? null,
    last_shift_id:  rec.last_shift_id  ?? null,
    last_shift_open:rec.last_shift_open ?? false,
  }));
}

/** Payload for toggling a register’s active flag (not used for shifts) */
export interface ToggleRegisterPayload {
  id_register:   number;
  active:        boolean;
  register_name?:string;
}
/** Response from POST /service_cash_flow/register/active */
export interface ToggleRegisterResponse {
  id_register: number;
  active:      boolean;
  register_name?: string;   // ← NEW, keep it optional
}
export async function toggleRegisterActive(
  payload: ToggleRegisterPayload
): Promise<ToggleRegisterResponse> {
  try {
    return await fetchData('/service_cash_flow/register/active', {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify(payload),
    });
  } catch (err: any) {
    const msg = String(err?.message ?? '');
    if (msg.includes('No state change requested')) {
      // treat “already the same” as success
      return { id_register: payload.id_register, active: payload.active };
    }
    throw err;   // bubble up real failures
  }
}

/** Payload for opening or closing a cash shift. */
export interface CashShiftPayload {
  id_register: number;
  open?:       boolean;  // omit or true → open; false → close
  shift_id?:   number;   // your UI code still passes this
}

/** Response from /cash_shift */
export interface CashShiftResponse {
  id_register: number;
  shift_id:    number;
  opened?:     boolean;
  closed?:     boolean;
}

/**
 * POST /service_cash_flow/cash_shift
 */
export async function cashShift(
  payload: CashShiftPayload
): Promise<CashShiftResponse> {
  // Build the minimal body, remapping shift_id → shift_id:
  const body: any = { id_register: payload.id_register };
  if (payload.open === false) {
    body.open     = false;
    body.shift_id = payload.shift_id;  // 👈 send `shift_id`
  }

  const res = await fetchData('/service_cash_flow/cash_shift', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  return {
    id_register: res.id_register,
    shift_id:    res.shift_id,
    opened:      res.opened,
    closed:      res.closed,
  };
}

/** Convenience: open a new shift on a register */
export function openCashShift(
  id_register: number
): Promise<CashShiftResponse> {
  return cashShift({ id_register });
}

/** Convenience: close an existing shift */
export function closeCashShift(
  id_register: number,
  shift_id:    number
): Promise<CashShiftResponse> {
  return cashShift({ id_register, open: false, shift_id });
}


export interface PaymentMethod {
  payment_method_id: number
  payment_method_name: string
  active: boolean
}

interface ListResponse {
  payment_methods: PaymentMethod[]
}

interface CreateResponse {
  payment_method_id: number
  active: boolean
}

interface ToggleResponse {
  payment_method_id: number
  active: boolean
}

/**
 * GET /payment_methods?action=list[&active_only=true]
 */
export async function fetchPaymentMethods(
  activeOnly = false
): Promise<ListResponse> {
  const query = activeOnly ? '?action=list&active_only=true' : '?action=list'
  return fetchData(`/service_cash_flow/payment_methods${query}`, {
    method: 'GET',
  })
}

/**
 * POST /payment_methods?action=add
 */
export async function createPaymentMethod(
  name: string
): Promise<CreateResponse> {
  return fetchData('/service_cash_flow/payment_methods', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'add',
      payment_method_name: name.trim(),
    }),
  })
}

/**
 * POST /payment_methods?action=set_active
 */
export async function setPaymentMethodActive(
  payment_method_id: number,
  active: boolean
): Promise<ToggleResponse> {
  return fetchData('/service_cash_flow/payment_methods', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'set_active',
      payment_method_id,
      active,
    }),
  })
}

export async function deleteRegister(id: number): Promise<void> {
  await fetchData(`/service_cash_flow/register/${id}`, { method: 'DELETE' });
}